import React, { useState } from 'react';
import { Container } from '../../components/Container/Container';
import { Card } from '../../components/Card/Card';
import { Icon } from '../../components/Icon/Icon';
import { SortableList } from '../../components/SortableList/SortableList';
import { usePaginatedResponse, useData } from '../../model/data';
import { getCategories, getCategoryName } from '../../model/category';
import { serializeQuery } from '../../model/filter';
import { CategoryForm } from './CategoryForm';
import { categoryPath } from '../../routes';
import { useMeta } from '../../model/meta';
import { Pagination } from '../../components/Pagination/Pagination';
import { getShopName } from '../../model/shop';
import { useQueryAsState } from '../../model/history';
import { formatDateTime } from '../../util/util';
import { FilterBar } from '../../components/FilterBar/FilterBar';
import { t } from '../../locales/translate';
import { compact } from 'lodash';
import { Modal } from '../../components/Modal/Modal';
import { SortByIcon } from '../../components/SortableList/SortByIcon';

export function Categories(): JSX.Element {
  const [showAddModal, setShowAddModal] = useState(false);
  const [params] = useQueryAsState();

  const paginator = usePaginatedResponse(
    (page, perPage) =>
      getCategories({
        types: ['artist', 'brand', 'category', 'label'],
        page,
        perPage,
        term: serializeQuery(params.query),
        sortBy: params.sortBy,
        sortDir: params.sortDir,
      }),
    JSON.stringify(params)
  );

  const categories = useData((db) => compact(paginator.currentIds.map((e) => db.table('categories').find(e))));

  useMeta({ breadcrumbs: [{ title: t.model.product.merchandise }, { title: t.model.category._plural }] });

  return (
    <Container className="companies">
      <Card
        className="companies__list"
        title={t.model.category._plural}
        elevation={2}
        actions={
          <Icon
            name="plus"
            onClick={() => {
              setShowAddModal(true);
            }}
          />
        }
        pagination={<Pagination {...paginator} />}
        fullbleed
      >
        <SortableList
          items={categories.map((cat) => {
            return {
              category: cat,
              shop: getShopName(cat.shopId),
              parent: getCategoryName(cat.parentCategory, { withShop: true }),
              name: cat.name,
              mainCategory: cat.mainCategory ? t.general.yes : t.general.no,
              order: cat.order,
              createdAt: formatDateTime(cat.createdAt),
              updatedAt: formatDateTime(cat.updatedAt),
            };
          })}
          loading={paginator.loading}
          actions={<FilterBar filters={['query']} />}
          columns={[
            {
              label: t.model.shop._name,
              apiSortIcon: <SortByIcon sortBy="shop" />,
              dataKey: 'shop',
              cellRenderer: (row) => row.category.shop?.name,
            },
            {
              label: t.model.shop.parent,
              dataKey: 'parent',
            },
            {
              label: t.general.name,
              apiSortIcon: <SortByIcon sortBy="name" />,
              dataKey: 'name',
            },
            {
              label: t.model.category.mainCategory,
              dataKey: 'mainCategory',
            },
            {
              label: t.model.tour.sortOrder,
              apiSortIcon: <SortByIcon sortBy="order" />,
              dataKey: 'order',
            },
            {
              label: t.model.datetime.createdAt,
              dataKey: 'createdAt',
            },
            {
              label: t.model.datetime.updatedAt,
              dataKey: 'updatedAt',
            },
          ]}
          rowLink={(row) => categoryPath(row.category)}
        />
      </Card>
      <Modal open={showAddModal} title={t.actions.add(t.model.category._name)} size="small">
        <CategoryForm model={{}} onClose={() => setShowAddModal(false)} />
      </Modal>
    </Container>
  );
}
